import { useState } from 'react';

/**
 * Social MVP — "Join a friend" entry. The player types the invite code
 * a friend shared (e.g. {@code GTC-4F2A}); {@link onJoin} resolves it to
 * a room and drops them into that table (the App's join-by-code handler
 * fires the normal JOIN_ROOM, displacing a bot). On success the app
 * flips into the room and this modal unmounts with it; on a bad code we
 * surface an inline error and stay open.
 */
export interface JoinByCodeModalProps {
  onClose: () => void;
  /** Resolve + join. Rejects (throws) on an unknown / archived code so
   *  the modal can show an error without closing. */
  onJoin: (code: string) => Promise<void>;
}

export function JoinByCodeModal({ onClose, onJoin }: JoinByCodeModalProps) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    void onJoin(trimmed)
      .then(() => {
        // onJoin flips the app into the room; this modal unmounts with
        // the view change. Nothing to do here.
      })
      .catch(() => {
        setError('No table found for that code. Check it and try again.');
        setBusy(false);
      });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Join a friend's table"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(8, 4, 22, 0.72)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        fontFamily: '"Fredoka", system-ui',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 360,
          borderRadius: 22,
          padding: '26px 22px 22px',
          textAlign: 'center',
          color: '#fff',
          background:
            'radial-gradient(ellipse 420px 260px at 50% 0%, rgba(139,92,246,0.28) 0%, transparent 62%), linear-gradient(180deg, #1c0e36 0%, #120726 100%)',
          border: '1.5px solid rgba(255,228,138,0.35)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: 'rgba(252,228,240,0.75)',
          }}
        >
          Play with friends
        </div>
        <h2
          style={{
            margin: '6px 0 4px',
            fontSize: 26,
            fontWeight: 900,
            color: '#ffe48a',
            textShadow: '0 2px 14px rgba(255,200,100,0.4)',
          }}
        >
          Join a table
        </h2>
        <p
          style={{
            margin: '0 0 18px',
            fontSize: 13,
            lineHeight: 1.45,
            color: 'rgba(255,255,255,0.65)',
            fontWeight: 500,
          }}
        >
          Enter the code a friend shared with you.
        </p>

        <input
          value={code}
          autoFocus
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={12}
          placeholder="GTC-XXXX"
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 16px',
            borderRadius: 14,
            border: `1.5px solid ${error ? '#ff6b8a' : 'rgba(255,228,138,0.4)'}`,
            background: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontFamily: 'inherit',
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 3,
            textAlign: 'center',
            textTransform: 'uppercase',
            outline: 'none',
          }}
        />

        {error ? (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              fontWeight: 600,
              color: '#ff9bb0',
            }}
          >
            {error}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '13px 0',
              borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.22)',
              background: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.85)',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || code.trim().length === 0}
            style={{
              flex: 1.4,
              padding: '13px 0',
              borderRadius: 999,
              border: '1.5px solid rgba(255,228,138,0.6)',
              background:
                busy || code.trim().length === 0
                  ? 'rgba(255,255,255,0.08)'
                  : 'linear-gradient(180deg, #ff6bd6 0%, #d63dc4 100%)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 900,
              letterSpacing: 0.5,
              cursor: busy || code.trim().length === 0 ? 'default' : 'pointer',
              opacity: busy || code.trim().length === 0 ? 0.55 : 1,
            }}
          >
            {busy ? 'Joining…' : 'Join'}
          </button>
        </div>
      </div>
    </div>
  );
}
